/**
 * Products.tsx — lista towarów tenanta na ConfigurableTable; kolumny DATA-DRIVEN z katalogu
 * (kartoteka-listify, wzorzec `PurchaseInvoices.tsx`).
 *
 * Kolumny rdzenne buduje `buildCoreColumns` z katalogu pól (`GET /system-modules/products/fields`):
 * pole z „Pokaż na liście" (showInList) + typ renderowalny = kolumna; klucz w wierszu odpowiedzi
 * wyliczany mechanicznie ze sluga (camelCase). Garstka kolumn ma bogaty render przez rejestr
 * nadpisań (`PRODUCT_OVERRIDES`): kod (mono), nazwa (+ badge „Z Symfonii"), typ (etykieta i18n),
 * cena bazowa (+ waluta), status (badge aktywny/nieaktywny), źródło (badge). Firma idzie
 * dedykowanym budowniczym (`buildCompanyColumn`) — `company_id` NIE jest polem katalogu towarów,
 * więc nie ma czego wykluczać z `buildCoreColumns` (brak `excludeSlugs`). Pola dodatkowe (ext)
 * przez `buildExtColumns`.
 *
 * Towary są read-only (pull z Symfonii, `sync_products`) — brak edycji/kasowania/dodawania
 * ręcznego w V1, stąd przycisk „Dodaj" wyszarzony, a klik wierszem → `/products/:id` (detal).
 *
 * Akcje zbiorcze (PHASE 25) rozlane wspólnym silnikiem `useListActions`: menu „Akcje" nad
 * listą (`ListActionsMenu`), prawy klik na zaznaczeniu (`onRowContextMenu`) i kebab „⋯" w
 * wierszu (`RowActionsMenu` z pozycjami katalogu). Eksport/import nie mają jeszcze endpointu
 * dla towarów → wyświetlają się jako „wkrótce" (brak handlera). Bramka „Wszystkie firmy"
 * blokuje archiwizację/import. UWAGA: backend towarów wystawia dziś tylko GET + PATCH
 * (kategoria) — brak `DELETE /:id`/restore, więc archiwizacja/przywracanie zaświecą się w UI,
 * ale wykonanie zwróci błąd do czasu dołożenia endpointów (decyzja właścicielki).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Plus } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListTopWidgets,
  ListViewModeToggle,
  QuickFilterBar,
  RowActionsMenu,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import {
  type ListResponseMeta,
  useServerListControls,
} from '@/components/configurable-table/useServerListControls';
import { ModuleGuard } from '@/components/ModuleGuard';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import {
  buildCompanyColumn,
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type CoreColumnOverride,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListActions } from '@/lib/list-actions/useListActions';
import { useEngineListExport } from '@/lib/list-export/useEngineListExport';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';

const PRODUCTS_KEY = 'products';

/**
 * `id` (klucz React) i `name` (kolumna `hideable: false`) są jedynymi polami GWARANTOWANYMI
 * w każdej odpowiedzi — reszta zależy od aktualnie widocznych kolumn (projekcja `?columns=`
 * zwraca tylko zażądane pola).
 */
interface ProductRow {
  id: string;
  companyId?: string;
  code?: string | null;
  name: string;
  productType?: string;
  barcode?: string | null;
  pkwiu?: string | null;
  cn?: string | null;
  fiscalName?: string | null;
  unit?: string | null;
  vatRate?: string | null;
  currencyCode?: string;
  purchasePrice?: string | null;
  basePrice?: string | null;
  category?: string | null;
  catalogPath?: string | null;
  weight?: string | null;
  isActive?: boolean;
  source?: string;
  description?: string | null;
  /** Nazwa kategorii z `record_categories` (LEFT JOIN w deskryptorze listy). Kolumna RĘCZNA. */
  categoryName?: string | null;
  /**
   * Wartości pól dodatkowych (ext) — obecne TYLKO gdy backend odpowiada w trybie
   * projekcji `?columns=` i zażądano ≥1 aktywnego pola ext.
   */
  ext?: Record<string, unknown>;
}

interface ListResponse {
  products: ProductRow[];
  limit: number;
  offset: number;
  total: number;
  /** Slugi kolumn CORE sortowalnych serwerowo (silnik list) — patrz `useServerListControls`. */
  meta?: ListResponseMeta;
  /** Etykiety pól `reference` (sibling do wierszy) — patrz `ExtRefLabels`. */
  extRefLabels?: ExtRefLabels;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function formatPrice(value: string | null | undefined, currency: string): string {
  if (value == null) return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(num);
}

/**
 * Rejestr nadpisań kolumn rdzennych towarów (per-kartoteka) — render bogaty tam, gdzie generyk
 * (typ→komórka) nie wystarcza. Sortowalność bierze z `ctx.resolveSortable(ctx.field)` (ta sama
 * serwerowa reguła co generyk — `meta.sortable` z ostatniej odpowiedzi). Klucze = slugi pól
 * z katalogu (`system_module_fields.slug`).
 */
const PRODUCT_OVERRIDES: Record<string, CoreColumnOverride<ProductRow>> = {
  code: ({ field, accessor, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 140,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: { category: 'text', fieldSlug: 'code' },
    renderCell: (value) => (
      <span className="font-mono text-xs text-text-secondary">{value ? String(value) : '—'}</span>
    ),
  }),
  // Nazwa + inline badge „Z Symfonii" (companion `source` dołączany do projekcji w komponencie).
  name: ({ field, accessor, t, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 280,
    hideable: false,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: { category: 'text', fieldSlug: 'name' },
    renderCell: (_value, row) => (
      <span className="flex items-center gap-2 text-text-primary">
        {row.name}
        {row.source === 'symfonia' && (
          <span className="rounded bg-accent-500/10 px-1.5 py-0.5 text-[10px] font-medium text-accent-500">
            {t('tenant.products.fromSymfonia')}
          </span>
        )}
      </span>
    ),
  }),
  // Typ towaru (good/service/set) — raw kod z backendu mapowany na etykietę i18n, nie przez
  // słownik katalogu (`tenant.products.type.*`).
  product_type: ({ field, accessor, t, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 120,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: {
      category: 'select',
      fieldSlug: 'product_type',
      transformLabel: (v) =>
        v == null ? '—' : t(`tenant.products.type.${v}`, { defaultValue: v }),
    },
    renderCell: (_value, row) => (
      <span className="text-text-secondary">
        {row.productType
          ? t(`tenant.products.type.${row.productType}`, { defaultValue: row.productType })
          : '—'}
      </span>
    ),
  }),
  // Jednostka miary — generyk mapowałby `varchar` na filtr tekstowy „zawiera" (utrata popupu
  // z listą wartości z `/products/distinct`, którą miała stara zaszyta kolumna) — override
  // wyłącznie po to, by zachować `category: 'select'`.
  unit: ({ field, accessor, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 100,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: { category: 'select', fieldSlug: 'unit' },
    renderCell: (value) => (
      <span className="text-text-secondary">{value ? String(value) : '—'}</span>
    ),
  }),
  // Kategoria — jak wyżej, override wyłącznie dla `category: 'select'` (parytet ze starą kolumną).
  category: ({ field, accessor, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 180,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: { category: 'select', fieldSlug: 'category' },
    renderCell: (value) => (
      <span className="text-text-secondary">{value ? String(value) : '—'}</span>
    ),
  }),
  // Cena bazowa + waluta w jednej komórce (companion `currency_code` dołączany do projekcji).
  base_price: ({ field, accessor, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 140,
    sortable: resolveSortable?.(field) ?? false,
    // Kolumna liczbowa → filtr zakresu (min/max), jak dałby generyk dla `numeric`
    // (mapExtFieldTypeToFilterCategory: numeric → 'number'). Backend wystawia `base_price`
    // jako filtrowalne (meta.filterable); override inaczej połknąłby popup filtra.
    headerFilter: { category: 'number', fieldSlug: 'base_price' },
    dataType: 'number',
    renderCell: (_value, row) => (
      <span className="tabular-nums text-text-primary">
        {formatPrice(row.basePrice, row.currencyCode ?? 'PLN')}
      </span>
    ),
  }),
  is_active: ({ field, t, resolveSortable }) => ({
    id: field.slug,
    accessor: (row) => (row.isActive ? 'true' : 'false'),
    header: field.name,
    defaultVisible: true,
    defaultWidth: 110,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: {
      category: 'select',
      fieldSlug: 'is_active',
      transformLabel: (v) => {
        if (v === 'true') return t('common.yes');
        if (v === 'false') return t('common.no');
        return '(Puste)';
      },
    },
    renderCell: (_value, row) =>
      row.isActive ? (
        <span className="inline-flex rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
          {t('tenant.products.active')}
        </span>
      ) : (
        <span className="inline-flex rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
          {t('tenant.products.inactive')}
        </span>
      ),
  }),
  source: ({ field, accessor, t, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 120,
    sortable: resolveSortable?.(field) ?? false,
    headerFilter: { category: 'select', fieldSlug: 'source' },
    renderCell: (_value, row) =>
      row.source === 'symfonia' ? (
        <Badge color="accent">{t('tenant.products.fromSymfonia')}</Badge>
      ) : (
        <Badge color="gray">{row.source}</Badge>
      ),
  }),
};

export function Products() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const scope = useActiveCompanyScope();
  const companyId = companyIdParam(scope);
  const { me } = useTenantAuth();
  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Sort serwerowy + paginacja (silnik list). Id kolumny = slug wszędzie (brak remapów) —
  // `columnIdToSortSlug` pominięty, fallback wewnętrzny `useServerListControls` wystarcza.
  const controls = useServerListControls();
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Filtry per kolumna z popupu w nagłówku + wiersz „Szukaj…" pod nagłówkami.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );
  const handleColumnFiltersChange = useCallback(
    (next: ColumnFilters) => {
      setColumnFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );

  useEffect(() => {
    controls.resetPage();
  }, [companyId, controls.resetPage]);

  // Wymuszony layout listy z pozycji Kreatora menu, z której user przyszedł
  // (kartoteka-layout-per-menu) — ta sama zmienna `tableId` musi iść do WSZYSTKICH
  // konsumentów niżej (ListTopWidgets, ListFilterBar, ConfigurableTable), inaczej
  // widżety i tabela renderują dwa różne layouty.
  const withMenuItem = useWithMenuItem();
  const forcedListLayoutId = useForcedListLayoutId(PRODUCTS_KEY);
  const tableId = forcedListLayoutId
    ? `${PRODUCTS_KEY}::layout-${forcedListLayoutId}`
    : PRODUCTS_KEY;

  // Szybkie filtry (presety z list_config) — chipy nad listą.
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId);
  const quickFilters = useMemo(
    () => listLayout?.layoutConfig?.quickFilters ?? [],
    [listLayout?.layoutConfig?.quickFilters],
  );
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);
  // Tryb widoku + tryby dostępne w bieżącym layoucie (raport tabeli) — wspólny hook.
  const viewModes = useListViewMode();
  useEffect(() => {
    if (activeQuickFilterId === null && quickFilters.length > 0) {
      const def = quickFilters.find((q) => q.isDefault);
      if (def) setActiveQuickFilterId(def.id);
    }
  }, [quickFilters, activeQuickFilterId]);

  // Zbiór kolumn FAKTYCZNIE widocznych teraz — zasilany przez `onVisibleColumnsChange`. `null`
  // przed pierwszym wywołaniem → `visibleColumnParams` spada na `listLayout`/`defaultVisible`.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);

  // Zaznaczenie wierszy (checkboxy) — steruje akcjami zbiorczymi (menu „Akcje", prawy klik).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Katalog pól modułu `products` (core + ext) — źródło WSZYSTKICH kolumn generowanych dynamicznie.
  const fieldsQ = useSystemModuleFields('products');
  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`), aktualizowany niżej PO `listQ`.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();

  const extColumns = useMemo<ColumnDefinition<ProductRow>[]>(
    () => buildExtColumns<ProductRow>(fieldsQ.data ?? [], t, getRefLabels),
    [fieldsQ.data, t, getRefLabels],
  );

  // Kolumny rdzenne z katalogu (klucz mechaniczny ze sluga; garstka przez rejestr nadpisań).
  // `company_id` nie jest polem katalogu towarów (dedykowany builder niżej) — brak excludeSlugs.
  const coreColumns = useMemo<ColumnDefinition<ProductRow>[]>(
    () =>
      buildCoreColumns<ProductRow>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels,
        overrides: PRODUCT_OVERRIDES,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
      }),
    [
      fieldsQ.data,
      t,
      getRefLabels,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
    ],
  );

  const columns = useMemo<ColumnDefinition<ProductRow>[]>(
    () => [
      ...coreColumns,
      buildCompanyColumn<ProductRow>({ companies, getRefLabels, t }),
      // Kolumna „Kategoria" (oś record_categories) — nazwa z LEFT JOIN (accessor `categoryName`).
      // Slug backendu `record_category` (NIE `category` — ten jest zajęty przez legacy kod rodzaju
      // z Symfonii). Kolumna RĘCZNA: filtr tekstowy po nazwie (`conditionOnly` — kolumna liczona
      // z joina), sort serwerowy po slugu `record_category` (parytet z Employees.tsx).
      {
        id: 'record_category',
        accessor: 'categoryName',
        header: t('tenant.recordCategories.picker.label'),
        defaultVisible: true,
        defaultWidth: 180,
        sortable: controls.isCoreColumnSortable('record_category', sortableSlugs),
        renderCell: (value) =>
          value == null || value === '' ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            String(value)
          ),
        headerFilter: { category: 'text', fieldSlug: 'record_category', conditionOnly: true },
      },
      ...extColumns,
    ],
    [
      coreColumns,
      companies,
      getRefLabels,
      t,
      extColumns,
      controls.isCoreColumnSortable,
      sortableSlugs,
    ],
  );

  // Mapa id kolumny → slug backendu dla `?columns=`. id = slug wszędzie poza „Firmą" (id `company`)
  // i „Kategorią" (id `record_category`, kolumna wirtualna z joina — mapowana jawnie).
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = {
      company: 'company_id',
      record_category: 'record_category',
    };
    for (const c of coreColumns) map[c.id] = c.id; // id = slug (buildCoreColumns)
    return map;
  }, [coreColumns]);

  // Kolumny formatowania warunkowego mogą celować w kolumnę NIEwidoczną — `extraColumnIds` dopisuje
  // jej slug do `?columns=`, żeby reguła miała dane do ewaluacji.
  const formattingRuleColumnIds = useMemo(
    () => listLayout?.layoutConfig?.formattingRules?.map((r) => r.columnId) ?? [],
    [listLayout?.layoutConfig?.formattingRules],
  );

  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns:
          visibleColumnIds !== null
            ? columns.map((c) => ({ id: c.id, visible: visibleColumnIds.includes(c.id) }))
            : listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet: new Set(extColumns.map((c) => c.id)),
        companions: [
          { whenSlug: 'name', addSlug: 'source' },
          { whenSlug: 'base_price', addSlug: 'currency_code' },
        ],
        extraColumnIds: formattingRuleColumnIds,
        // Pola kalendarza układu zawsze w `?columns=` — kalendarz nie zależy od widocznych kolumn.
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      visibleColumnIds,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extColumns,
      formattingRuleColumnIds,
    ],
  );

  // Filtry do backendu jako pojedyncza tablica `headerFilters`:
  // (1) popup w nagłówku (mode=values/condition/range) +
  // (2) input „Szukaj…" pod headerem (konwersja na mode=condition contains) +
  // (3) aktywny szybki filtr (preset).
  const viewHeaderFilters = useMemo(() => {
    const merged: ColumnHeaderFilter[] = [];

    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }

    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }

    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    if (activeQf) {
      for (const cond of activeQf.filters) {
        const col = columns.find((c) => c.id === cond.field);
        const fieldSlug = col?.headerFilter?.fieldSlug;
        if (!fieldSlug) continue;
        merged.push({
          field: fieldSlug,
          mode: 'condition',
          operator: cond.operator as ColumnHeaderFilter['operator'],
          conditionValue: cond.value == null ? '' : String(cond.value),
        });
      }
    }

    return merged;
  }, [headerFilters, debouncedColumnFilters, quickFilters, activeQuickFilterId, columns]);
  // Widok kalendarza dociąga tylko rekordy z wyświetlanego miesiąca (wspólny filtr z silnika) —
  // tylko zapytanie listy. Eksport CSV (E16) bierze same filtry widoku: miesiąc to nawigacja.
  const exportHeaderFilters =
    viewHeaderFilters.length > 0 ? JSON.stringify(viewHeaderFilters) : undefined;
  const serializedHeaderFilters = controls.calendarHeaderFilter
    ? JSON.stringify([...viewHeaderFilters, controls.calendarHeaderFilter])
    : exportHeaderFilters;

  const queryKey = [
    PRODUCTS_KEY,
    {
      q: debouncedSearch,
      companyId: companyId ?? 'all',
      headerFilters: serializedHeaderFilters,
      columns: visibleColumnParams,
      ...controls.queryParams,
    },
  ];
  // Parametry filtrów listy BEZ stronicowania i projekcji kolumn — wspólne dla zapytania listy
  // i eksportu CSV (E16: plik zawiera dokładnie to, co lista). Zwykła funkcja, nie hook: czyta
  // stan strony w chwili wywołania (zapytanie listy / pobranie eksportu).
  function buildListFilterParams({
    includeCalendar,
  }: {
    includeCalendar: boolean;
  }): URLSearchParams {
    const listHeaderFilters = includeCalendar ? serializedHeaderFilters : exportHeaderFilters;
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (companyId) params.set('companyId', companyId);
    if (listHeaderFilters) params.set('headerFilters', listHeaderFilters);
    if (controls.queryParams.sort) params.set('sort', controls.queryParams.sort);
    params.set('order', controls.queryParams.order);
    return params;
  }

  const listQ = useQuery({
    queryKey,
    // Trzyma poprzednie dane podczas refetchu wywołanego zmianą klucza (widoczne kolumny się
    // „ustalają" asynchronicznie) — bez tego lista miga loaderem przy każdym wejściu.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = buildListFilterParams({ includeCalendar: true });
      if (visibleColumnParams.length > 0) {
        params.set('columns', visibleColumnParams.join(','));
      }
      params.set('limit', String(controls.queryParams.limit));
      params.set('offset', String(controls.queryParams.offset));
      const res = await api.get<ListResponse>(`/products?${params.toString()}`);
      return res.data;
    },
  });
  setRefLabels(listQ.data?.extRefLabels);
  // `meta.sortable`/`meta.filterable` w stan przez EFEKT, nie w renderze (to useState →
  // wywołanie w renderze pętli).
  useEffect(() => {
    setSortableSlugs(listQ.data?.meta?.sortable);
  }, [listQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(listQ.data?.meta?.filterable);
  }, [listQ.data?.meta?.filterable]);

  // Eksport CSV (E16) — dokładnie to, co lista. „Eksport listy": te same filtry co zapytanie
  // listy (wyszukiwarka, filtry kolumn, szybki filtr, sort, wybrana firma), wszystkie strony.
  // „Eksport zaznaczonych": tylko wskazane ID (+ firma i sort listy). Plik zawiera WYŁĄCZNIE
  // kolumny widoczne w tabeli, w jej kolejności i z jej nagłówkami.
  const listExport = useEngineListExport<ProductRow>({
    target: 'products',
    filename: 'towary',
    // Bez miesiąca kalendarza — to nawigacja widoku, nie filtr (plik = cały widok).
    buildFilterParams: () => buildListFilterParams({ includeCalendar: false }),
    columns,
    visibleColumnIds,
    // Dopóki katalog pól i układ listy się ładują, eksport nie startuje (niepełna lista kolumn).
    ready: fieldsQ.isSuccess && !layoutLoading,
    columnSlug: (id) => coreIdToSlug[id] ?? (extColumns.some((c) => c.id === id) ? id : undefined),
    // Waluta wiersza — komórka „Cena bazowa" formatuje kwotę w walucie towaru.
    extraSlugs: ['currency_code'],
    // Etykiety odwołań (firma, pola `reference`) z odpowiedzi eksportu — obejmują WSZYSTKIE
    // wyeksportowane wiersze, nie tylko bieżącą stronę listy. Te same buildery co tabela.
    buildExportColumns: (refLabels) => {
      const getLabels = () => refLabels as ExtRefLabels | undefined;
      return [
        ...buildCoreColumns<ProductRow>({
          fields: fieldsQ.data ?? [],
          t,
          getRefLabels: getLabels,
          overrides: PRODUCT_OVERRIDES,
        }),
        buildCompanyColumn<ProductRow>({ companies, getRefLabels: getLabels, t }),
        ...columns.filter((c) => c.id === 'record_category'),
        ...buildExtColumns<ProductRow>(fieldsQ.data ?? [], t, getLabels),
      ];
    },
    // Komórka „Nazwa" na ekranie to nazwa + plakietka „Z Symfonii" — do pliku idzie sama nazwa
    // (źródło ma własną kolumnę).
    textOverrides: { name: (row) => row.name ?? '' },
    layoutColumns: listLayout?.layoutConfig?.columns,
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: t('tenant.products.list.title'),
  });

  // Akcje listy (PHASE 25) — generyczny hook (resolver widoczności + wykonawca +
  // menu kontekstowe + potwierdzenia). Eksporty (zaznaczonych i całej listy) składa strona
  // z widocznych kolumn (`handlers`); towary są read-only (pull z Symfonii) i nie mają modalu
  // importu → import wyszarzony „wkrótce" (brak handlera). Archiwizacja/przywracanie idą generykiem (rozgłoszenie po ID na
  // `/:entity/:id`). Bramka „Wszystkie firmy" blokuje archiwizację/import — encja jest
  // firmowo-scope'owana, backend wymaga X-Company-Id, którego interceptor w tym trybie nie wysyła.
  const productActions = useListActions({
    kartotekaKey: 'products',
    entity: 'products',
    queryKey: [PRODUCTS_KEY],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    handlers: { ...listExport.handlers },
    guard: (actionId) =>
      (actionId === 'delete' || actionId === 'import') && scope.isAll
        ? t('common.allModeActionBlocked')
        : null,
    testIdPrefix: 'products',
  });

  const products = listQ.data?.products ?? [];

  return (
    <ModuleGuard moduleCode="crm_core">
      <main
        className="flex h-full min-h-0 flex-col bg-bg-secondary p-6"
        data-testid="products-page"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-text-primary">
                {t('tenant.products.list.title')}
              </h1>
              <p className="mt-1 text-sm text-text-tertiary">
                {t('tenant.products.list.subtitle')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="products" />
              <ListActionsMenu {...productActions.menuProps} />
              <ListBuilderMenu slug="products" testIdPrefix="products" />
              {/* Towary read-only/pull-only V1 — „Dodaj towar" wyszarzone. */}
              <Button
                variant="primary"
                size="sm"
                disabled
                leftIcon={<Plus size={16} aria-hidden="true" />}
                data-testid="products-add-btn"
              >
                {t('tenant.products.list.addBtn')}
              </Button>
            </div>
          </header>

          <div className="shrink-0">
            <ListTopWidgets tableId={tableId} companyId={companyId} />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {fieldsQ.isError ? (
              <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
            ) : (
              <ConfigurableTable<ProductRow>
                tableId={tableId}
                testIdPrefix="products"
                showOrdinal
                fillHeight
                {...viewModes.tableProps}
                /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): segmented po lewej,
                   baton odśwież/filtry po prawej. */
                listToolbar={
                  <div className="flex flex-wrap items-center gap-2">
                    <QuickFilterBar
                      quickFilters={quickFilters}
                      activeId={activeQuickFilterId}
                      onChange={(id) => {
                        setActiveQuickFilterId(id);
                        controls.resetPage();
                      }}
                    />
                    <div className="ml-auto flex shrink-0 items-center gap-2">
                      <ListFilterBar
                        tableId={tableId}
                        columns={columns}
                        search={search}
                        onSearchChange={(next) => {
                          setSearch(next);
                          controls.resetPage();
                        }}
                        searchPlaceholder={t('tenant.products.list.searchPlaceholder')}
                        showSearch={false}
                        headerFilters={headerFilters}
                        onHeaderFiltersChange={handleHeaderFiltersChange}
                        onRefresh={() =>
                          queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] })
                        }
                        testIdPrefix="products"
                      />
                    </div>
                  </div>
                }
                columns={columns}
                data={products}
                isLoading={listQ.isLoading}
                getRowId={(row) => row.id}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onRowContextMenu={productActions.onRowContextMenu}
                headerFilters={headerFilters}
                onHeaderFiltersChange={handleHeaderFiltersChange}
                filterable
                columnFilters={columnFilters}
                onColumnFiltersChange={handleColumnFiltersChange}
                onVisibleColumnsChange={setVisibleColumnIds}
                {...controls.tableProps}
                totalItems={listQ.data?.total ?? 0}
                isFetching={listQ.isFetching}
                onCalendarRangeChange={controls.setCalendarRange}
                onRowClick={(row) => navigate(withMenuItem(`/products/${row.id}`))}
                renderActions={(row) => {
                  const items = productActions.rowCatalogActionItems(row.id);
                  if (items.length === 0) return null;
                  // Towary nie mają natywnych akcji wiersza (read-only) — zdejmij wiodący
                  // separator z pierwszej pozycji katalogu: dywider oddziela akcje katalogu OD
                  // akcji natywnych, których tu nie ma (inaczej menu zaczyna się kreską).
                  const menuItems = items.map((it, i) =>
                    i === 0 ? { ...it, dividerBefore: false } : it,
                  );
                  return (
                    <div className="flex justify-end">
                      <RowActionsMenu testId={`products-row-${row.id}-actions`} items={menuItems} />
                    </div>
                  );
                }}
                actionsColumnLabel={t('common.actions')}
                emptyIcon={Package}
                emptyTitle={t('tenant.products.list.empty')}
              />
            )}
          </div>
        </div>
        {productActions.overlays}
      </main>
    </ModuleGuard>
  );
}
